import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Close, Logout, Menu } from '@carbon/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { ROUTES, GROUP_ORDER, routeOpen } from '../routes';
import { openStream } from '../api/client';
import { kiemBan } from '../components/banCms';
import logo from '../assets/ruby-logo.webp';

// topics pushed by the API; each invalidates the queries that start with the same key
const TOPIC_QUERIES: Record<string, string[]> = {
  commands: ['commands', 'command', 'users', 'user', 'ledger', 'comp-batches', 'comp-batch'],
  dashboard: ['dashboard'],
  alerts: ['dashboard', 'alerts'],
  wallet: ['wallet'],
  payouts: ['agent-payouts'],
};

// "Chu so huu" -> "CS"
const initials = (s: string) => s.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase();

export function Shell({ children }: { children: ReactNode }) {
  const { me, logout, can } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const [live, setLive] = useState(false);
  const loc = useLocation();
  const qc = useQueryClient();

  useEffect(() => openStream((ev) => {
    for (const k of TOPIC_QUERIES[ev.topic] ?? []) qc.invalidateQueries({ queryKey: [k] });
  }, setLive), [qc]);

  useEffect(() => { setNavOpen(false); }, [loc.pathname]);
  // tab mo tu truoc mot lan deploy: chuyen trang thi tai trang moi tu may chu (ban moi), khong chay code cu tren API moi
  useEffect(() => { void kiemBan(); }, [loc.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const visible = ROUTES.filter((r) => r.label && routeOpen(r, can, me?.role));
  const groups = GROUP_ORDER.map((g) => ({ g, items: visible.filter((r) => r.group === g) })).filter((x) => x.items.length);
  // the nav entry of the page: the longest listed path the URL starts with, at a segment boundary
  // (/insights/games/Sicbo is "Hiệu quả game", not "Tổng quan điều hành")
  const current = visible.map((r) => r.path)
    .filter((p) => (p === '/' ? loc.pathname === '/' : loc.pathname === p || loc.pathname.startsWith(p + '/')))
    .sort((a, b) => b.length - a.length)[0];
  const name = me?.displayName ?? '';
  const role = me && me.roleLabel !== me.displayName ? me.roleLabel : '';
  const liveText = live ? 'Đang cập nhật trực tiếp' : 'Mất kết nối trực tiếp';

  return (
    <div className={'shell' + (navOpen ? ' is-open' : '')}>
      <a className="skip" href="#main">Tới nội dung</a>
      <aside className="shell__nav" id="shell-nav" aria-label="Điều hướng">
        <NavLink to="/" className="shell__brand" aria-label="Ruby.club, về Tổng quan">
          <img src={logo} alt="" width={124} height={100} />
          <span>Trang quản trị</span>
        </NavLink>
        <nav className="shell__links">
          {groups.map(({ g, items }) => (
            <div key={g} role="group" aria-label={g}>
              <p className="shell__group-label" aria-hidden="true">{g}</p>
              {items.map((r) => {
                const active = r.path === current;
                const Icon = r.icon;
                return (
                  <NavLink key={r.path} to={r.path} className={'shell__link' + (active ? ' is-active' : '')} aria-current={active ? 'page' : undefined}>
                    {Icon ? <Icon size={16} /> : null}
                    <span>{r.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="shell__me">
          <span className="shell__avatar" aria-hidden="true">{initials(name)}</span>
          <span className="shell__who"><b>{name}</b>{role ? <span>{role}</span> : null}</span>
          <span className="shell__spacer" />
          <button type="button" className="shell__icon-btn" aria-label="Đăng xuất" onClick={() => logout()}><Logout size={18} /></button>
        </div>
      </aside>
      <button type="button" className="shell__scrim" aria-label="Đóng menu" tabIndex={-1} onClick={() => setNavOpen(false)} />
      <header className="shell__top">
        <button type="button" className="shell__icon-btn shell__menu" aria-label={navOpen ? 'Đóng menu' : 'Mở menu'}
          aria-expanded={navOpen} aria-controls="shell-nav" onClick={() => setNavOpen(!navOpen)}>
          {navOpen ? <Close size={20} /> : <Menu size={20} />}
        </button>
        <NavLink to="/" className="shell__top-logo" aria-label="Ruby.club, về Tổng quan"><img src={logo} alt="" width={40} height={32} /></NavLink>
        <span className="shell__spacer" />
        <span className={'shell__live' + (live ? ' is-on' : '')} role="status" aria-label={liveText} title={liveText}>
          <span className="shell__live-text">{live ? 'Trực tiếp' : 'Mất kết nối'}</span>
        </span>
        <span className="shell__top-who">
          <span className="shell__avatar" aria-hidden="true">{initials(name)}</span>
          <span className="shell__who"><b>{name}</b>{role ? <span>{role}</span> : null}</span>
        </span>
        <button type="button" className="shell__icon-btn" aria-label="Đăng xuất" onClick={() => logout()}><Logout size={20} /></button>
      </header>
      <main id="main" className="shell__main">{children}</main>
    </div>
  );
}
